import React, { useMemo, useState } from 'react';
import Layout from '../components/Layout';
import '../css/DestinationsPage.css';
import { Destination } from '../types/Destination';
import { getDestinationFull } from '../api/Destinations';
import DestinationDetailsModal from '../components/destinations/DestinationDetailsModal';
import DestinationForm from '../components/destinations/DestinationForm';
import DestinationList from '../components/destinations/DestinationList';
import { useDestinationForm } from '../hooks/useDestinationForm';
import { useDestinationsData } from '../hooks/useDestinationsData';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const mediaRequirements = [
  'Hero image: 16:9 to 3:2, minimum 1920 x 1080, preferred 2560 x 1440.',
  'Destination card image: 4:3, preferred 1600 x 1200, crop-safe for small cards.',
  'Feature/gallery image: 16:10 or 4:3, preferred 2000 px wide.',
  'Gallery set: 3-6 real destination images with premium editorial quality.',
  'Recommended experience images: 16:9, minimum 1400 px wide, one per experience when available.',
  'Open Graph image: 1200 x 630, no text baked into the image.',
];

const DestinationsPage: React.FC = () => {
  const [detailDestination, setDetailDestination] = useState<Destination | null>(null);
  const [searchParams] = useSearchParams();

  const destinationFilters = useMemo(() => {
    const updatedAt =
      searchParams.get('updated_at') || searchParams.get('updatedAt') || undefined;
    const createdByRaw =
      searchParams.get('created_by') || searchParams.get('createdBy');
    const createdByNum = createdByRaw ? Number(createdByRaw) : NaN;
    const createdBy =
      createdByRaw && Number.isFinite(createdByNum) ? createdByNum : createdByRaw || undefined;
    return {
      ...(updatedAt ? { updatedAt } : {}),
      ...(createdBy ? { createdBy } : {}),
    };
  }, [searchParams]);

  const {
    destinations,
    loading,
    searchCity,
    searchResult,
    searchError,
    handleDelete,
    handleSearchInputChange,
    loadDestinations
  } = useDestinationsData(destinationFilters);

  const {
    formData,
    isEditMode,
    canSubmit,
    submitting,
    aiFilling,
    heroImageFile,
    seoOgImageFile,
    galleryImageFiles,
    recommendedExperienceImageFiles,
    handleFormField,
    updateCsvField,
    autofillFromAI,
    handleHeroImageFileChange,
    clearHeroImageFile,
    handleSeoOgImageFileChange,
    clearSeoOgImageFile,
    handleGalleryImageFileChange,
    clearGalleryImageFile,
    handleRecommendedExperienceImageFileChange,
    clearRecommendedExperienceImageFile,
    startEditing,
    cancelEditing,
    resetForm,
    handleSubmit
  } = useDestinationForm({ onCreated: loadDestinations });

  const handleStartEdit = async (destination: Destination) => {
    try {
      const fullDestination = await getDestinationFull(destination.slug);
      startEditing(fullDestination);
    } catch (error) {
      toast.error('Failed to load destination details for editing.');
    }
  };

  return (
    <Layout>
      <div className="destinations-page">
        <div className="destinations-container">
          <section className="destinations-form-section">
            <div className="form-header">
              <h2>{isEditMode ? 'Edit Destination' : 'Add Destination'}</h2>
            </div>

            <DestinationForm
              formData={formData}
              isEditMode={isEditMode}
              canSubmit={canSubmit}
              submitting={submitting}
              aiFilling={aiFilling}
              heroImageFile={heroImageFile}
              seoOgImageFile={seoOgImageFile}
              galleryImageFiles={galleryImageFiles}
              recommendedExperienceImageFiles={recommendedExperienceImageFiles}
              onSubmit={handleSubmit}
              onReset={isEditMode ? cancelEditing : resetForm}
              onFieldChange={handleFormField}
              onUpdateCsvField={updateCsvField}
              onGenerateAiDraft={autofillFromAI}
              onHeroImageFileChange={handleHeroImageFileChange}
              onClearHeroImageFile={clearHeroImageFile}
              onSeoOgImageFileChange={handleSeoOgImageFileChange}
              onClearSeoOgImageFile={clearSeoOgImageFile}
              onGalleryImageFileChange={handleGalleryImageFileChange}
              onClearGalleryImageFile={clearGalleryImageFile}
              onRecommendedExperienceImageFileChange={handleRecommendedExperienceImageFileChange}
              onClearRecommendedExperienceImageFile={clearRecommendedExperienceImageFile}
            />

            <section className="form-group" style={{ marginTop: '16px' }}>
              <div className="section-bar">
                <h3>Media Requirements</h3>
              </div>
              <p className="helper-text">
                Source:{' '}
                <a
                  href="/Users/ravik/work/otaprime/website-v2/docs/media-requirements.md"
                  target="_blank"
                  rel="noreferrer"
                >
                  media-requirements.md
                </a>
              </p>
              <ul style={{ margin: 0, paddingLeft: '20px', display: 'grid', gap: '8px' }}>
                {mediaRequirements.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          </section>

          <DestinationList
            destinations={destinations}
            searchValue={searchCity}
            searchError={searchError}
            searchResult={searchResult}
            loading={loading}
            onSearchChange={handleSearchInputChange}
            onView={setDetailDestination}
            onEdit={handleStartEdit}
            onDelete={handleDelete}
          />
        </div>
      </div>

      {detailDestination && (
        <DestinationDetailsModal
          destination={detailDestination}
          onClose={() => setDetailDestination(null)}
        />
      )}
    </Layout>
  );
};

export default DestinationsPage;
